'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { focusMemberControl } from '@core/lib/memberFocus';
import { PAPER_INPUT } from '@core/components/FormExecutor/inputStyles';
import { DOCK_TEXT_ACTION } from '@/components/session-phases/AnswerDockParts';

/**
 * The typed answer: the card shape of the answer dock (PromptingPhaseScreen).
 *
 * The question is not drawn here. It belongs to the screen, which keeps it in
 * place for the whole exchange, and on a scale question the scale sits above the
 * dock too: this field is then the optional elaboration.
 */
interface TextInputInterfaceProps {
  /** Names the field. A member who lands here by focus never reads the heading. */
  questionText: string;
  clarificationLeadIn?: string | null;
  onSubmit: (text: string) => void;
  minLength?: number;
  maxLength?: number;
  micAvailable: boolean;
  onSwitchToVoice: () => void;
  isSubmitting?: boolean;
  /**
   * A scale question. The score comes from the ScaleVisual above the dock and
   * is what the member must give; the text is optional.
   */
  scale?: {
    score: number | null;
    onSubmit: (score: number, text: string) => void;
  } | null;
  /**
   * The draft, when the screen owns it. The screen keeps the draft across a
   * switch to voice and back, so this field unmounting with the card does not
   * lose what the member typed. Uncontrolled when absent.
   */
  value?: string;
  onValueChange?: (value: string) => void;
  /**
   * Marks the render right after a member-initiated voice→text switch, so the
   * modality change is perceivable to keyboard and screen-reader users.
   *
   * Text mode focuses the field by default anyway (see the mount effect). What
   * this flag still does is override the scale-question carve-out and gate
   * `onFocusOnMountConsumed`.
   */
  focusOnMount?: boolean;
  /**
   * Fired once when a `focusOnMount` request has been honored. The owner must
   * clear its pending flag HERE, not on state change, so a delayed mount can
   * never miss the focus intent.
   */
  onFocusOnMountConsumed?: () => void;
  /**
   * Id of copy that must be announced along with the field (the resume
   * notice). The field takes focus on arrival, so anything rendered above it is
   * skipped by a screen reader, and the resume notice self-clears after 6s
   * (Codex r3 P2).
   */
  describedById?: string;
}

// Non-empty is the only gate. A 10-char minimum silently disabled Send for
// legitimate short answers ("Loved it." = 9 chars) with no explanation; the
// backend handles terse answers by design (low-substance reflection rules), so
// text must not be stricter than voice.
const DEFAULT_MIN_LENGTH = 1;
const DEFAULT_MAX_LENGTH = 5000;

const ANSWER_PLACEHOLDER = 'Type your response here...';
const SCALE_PLACEHOLDER = 'Tell us more about your rating (optional)...';

export function TextInputInterface({
  questionText,
  clarificationLeadIn,
  onSubmit,
  minLength = DEFAULT_MIN_LENGTH,
  maxLength = DEFAULT_MAX_LENGTH,
  micAvailable,
  onSwitchToVoice,
  isSubmitting,
  scale,
  value,
  onValueChange,
  focusOnMount,
  onFocusOnMountConsumed,
  describedById,
}: TextInputInterfaceProps) {
  const [ownText, setOwnText] = useState('');
  const text = value ?? ownText;
  const setText = (next: string) => {
    if (onValueChange) onValueChange(next);
    else setOwnText(next);
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isScale = scale != null;

  // Mount-only by design: these are snapshots of the arriving card, not live
  // subscriptions. The card mounts once per question in text mode, and again on
  // each switch into text, so mount focus is per-arrival focus. The consumed
  // guard keeps the "fires once" contract under StrictMode's dev-only effect
  // replay (refs survive the simulated remount).
  const focusOnMountRef = useRef(focusOnMount);
  const onFocusOnMountConsumedRef = useRef(onFocusOnMountConsumed);
  const isScaleOnMountRef = useRef(isScale);
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    const isSwitchHandshake = focusOnMountRef.current === true;
    let cancelFocus: (() => void) | undefined;
    // Text mode should cost zero taps per question, so the answer box takes
    // focus by default (WL-20260731-004). Scale questions opt out: there the
    // scale is the required control and this field is the optional
    // elaboration, so focusing it would point the member at the wrong control
    // and raise the mobile keyboard over the scale. An explicit voice→text
    // switch overrides that carve-out: the member just asked for the keyboard.
    if (isSwitchHandshake || !isScaleOnMountRef.current) {
      if (textareaRef.current) cancelFocus = focusMemberControl(textareaRef.current);
    }
    if (isSwitchHandshake && !focusConsumedRef.current) {
      focusConsumedRef.current = true;
      onFocusOnMountConsumedRef.current?.();
    }
    return cancelFocus;
  }, []);

  // The field carries its own accessible name: a screen-reader member lands
  // here directly and would otherwise never reach the question heading. Both
  // strings are the visible copy, so the announced name and the seen text
  // cannot drift.
  const placeholder = isScale ? SCALE_PLACEHOLDER : ANSWER_PLACEHOLDER;
  const promptLabel = clarificationLeadIn
    ? `${clarificationLeadIn} ${questionText}`
    : questionText;
  // On a scale question this field is the optional elaboration, not the answer,
  // so naming it with the question alone would mislabel the required control.
  const inputLabel = isScale ? `${SCALE_PLACEHOLDER} ${promptLabel}` : promptLabel;

  const canSubmit = isScale
    ? scale.score != null && !isSubmitting
    : text.trim().length >= minLength && !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (isScale && scale.score != null) {
      scale.onSubmit(scale.score, text.trim());
    } else {
      onSubmit(text.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, maxLength))}
        onKeyDown={handleKeyDown}
        rows={isScale ? 3 : 4}
        data-testid="text-answer-input"
        data-ph-no-capture
        aria-label={inputLabel}
        aria-describedby={describedById}
        placeholder={placeholder}
        className={`${PAPER_INPUT} resize-none`}
      />

      <div className="flex items-center justify-between gap-3">
        {micAvailable ? (
          // The typed draft is kept, never discarded: the screen owns it, so
          // switching to voice and back brings it back as it was.
          <button
            type="button"
            onClick={onSwitchToVoice}
            data-testid="switch-to-voice"
            className={DOCK_TEXT_ACTION}
          >
            Speak instead
          </button>
        ) : (
          <span />
        )}
        <motion.button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="text-submit"
          whileTap={canSubmit ? { scale: 0.97 } : undefined}
          className="min-h-[44px] rounded-[12px] bg-accent-dark px-9 py-3.5 font-body text-[17px] font-semibold tracking-[0.01em] text-[var(--cream)] cursor-pointer enabled:hover:shadow-[0_4px_14px_rgba(36,114,114,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-shadow duration-200"
        >
          {isSubmitting ? 'Sending...' : 'Send'}
        </motion.button>
      </div>
    </div>
  );
}
